import React, { useEffect, useRef, useState } from "react";
import ConversationLineSettings from "./ConversationLineSettings";
import { useConversationLines } from "./ConversationLines.hook";
import ConversationPreviewInput from "./ConversationPreviewInput";
import ConversationPreviewMessageView from "./ConversationPreviewMessageView";

/**
 * Chat plus, when a line is selected, its settings beside it (Design Studio
 * `screens/lookat-rework/cv2c-chat-closed.html` / `cv2b-chat-panel.html`).
 */
export default function ConversationPreviewBody() {
  const { conversation, deleteLine } = useConversationLines();
  const messageViewRef = useRef<HTMLDivElement>(null);
  const [selectedLineId, setSelectedLineId] = useState<string | undefined>(undefined);

  useEffect(() => {
    setSelectedLineId(undefined);
  }, [conversation.id]);

  // Close the panel when its line is gone (deleted, or undone away).
  const selectedExists = conversation.messages.some((m) => m.id === selectedLineId);
  useEffect(() => {
    if (selectedLineId && !selectedExists) setSelectedLineId(undefined);
  }, [selectedLineId, selectedExists]);

  // Escape closes the panel, unless something on top of it takes that Escape
  // first: an open chip menu, the Animation Library, a popover menu, or the
  // rich text editor's own suggestions.
  const isPanelOpen = Boolean(selectedLineId && selectedExists);
  useEffect(() => {
    if (!isPanelOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const overlayOpen = document.querySelector(
        '[data-radix-popper-content-wrapper], [role="dialog"], [role="menu"], .MuiPopover-root, .MuiModal-root'
      );
      if (overlayOpen) return;
      setSelectedLineId(undefined);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isPanelOpen]);

  const onSubmitClick = () => {
    setTimeout(() => {
      if (messageViewRef.current) {
        messageViewRef.current.scrollTop = messageViewRef.current.scrollHeight;
      }
    }, 100);
  };

  const chat = (
    <div className="flex flex-col min-w-0 min-h-0 h-full gap-5">
      <ConversationPreviewMessageView
        ref={messageViewRef}
        selectedLineId={selectedLineId}
        onSelectLine={(line) => setSelectedLineId(line.id)}
        onDeleteLine={(line) => deleteLine(line.id)}
      />
      {/* While a line's settings are open you're editing, not writing: the composer steps
          aside so the chat keeps its width and height (Design Studio cv3-chat-panel). */}
      {!isPanelOpen && <ConversationPreviewInput onSubmitClick={onSubmitClick} />}
    </div>
  );

  if (!selectedLineId || !selectedExists) {
    return <div className="w-full h-full min-h-0">{chat}</div>;
  }

  return (
    <div className="w-full h-full min-h-0 grid grid-cols-[minmax(0,1fr)_380px] gap-6">
      {chat}
      <ConversationLineSettings
        lineId={selectedLineId}
        onClose={() => setSelectedLineId(undefined)}
      />
    </div>
  );
}
